import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import VerifyOtp from "@/pages/VerifyOtp";
import VerifyTotp from "@/pages/VerifyTotp";
import ResetPassword from "@/pages/ResetPassword";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import Dashboard from "@/pages/Dashboard";
import Peserta from "@/pages/Peserta";
import RiwayatImport from "@/pages/RiwayatImport";
import PesertaTerhapus from "@/pages/PesertaTerhapus";
import Prodi from "@/pages/Prodi";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import Keaktifan from "@/pages/Keaktifan";
import PengaturanPeriode from "@/pages/PengaturanPeriode";
import PengaturanTarif from "@/pages/PengaturanTarif";
import PengaturanKeamananAkun from "@/pages/PengaturanKeamananAkun";
import LaporanUnitCost from "@/pages/LaporanUnitCost";
import RotasiKlinik from "@/pages/RotasiKlinik";
import OrientasiRS from "@/pages/OrientasiRS";
import RotasiKlinikPerInstalasi from "@/pages/RotasiKlinikPerInstalasi";
import DataRotasiKlinik from "@/pages/DataRotasiKlinik";
import PeerAssessmentAdmin from "@/pages/PeerAssessmentAdmin";
import PeerAssessmentPublic from "@/pages/PeerAssessmentPublic";
import SurveiTautanBerubah from "@/pages/SurveiTautanBerubah";
import RuteSisa from "@/components/RuteSisa";
import SurveiAdmin from "@/pages/SurveiAdmin";
import AppLayout from "@/components/AppLayout";
import "@/index.css";
import { PERAN_KEAKTIFAN, PERAN_LIHAT_SURVEI, halamanAwal, halamanDiizinkan } from "@/lib/roles";

function ProtectedRoute({ children, roles = null }) {
    const { user, checking } = useAuth();
    const { pathname } = useLocation();
    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500">
                Memuat sesi...
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (user.must_change_password) return <Navigate to="/ubah-password" replace />;
    // Admin Instalasi hanya boleh ke halamannya sendiri (lib/roles.js); server tetap menolak API lain.
    if (!halamanDiizinkan(user.role, pathname)) return <Navigate to={halamanAwal(user.role)} replace />;
    if (roles && !roles.includes(user.role)) return <Navigate to={halamanAwal(user.role)} replace />;
    return children;
}

function PublicOnly({ children }) {
    const { user, checking } = useAuth();
    if (checking) return null;
    if (user && user.must_change_password) return <Navigate to="/ubah-password" replace />;
    if (user) return <Navigate to={halamanAwal(user.role)} replace />;
    return children;
}

function App() {
    return (
      <ThemeProvider>
        <BrowserRouter>
            {/* AuthProvider DI DALAM BrowserRouter (bukan sebaliknya) -- supaya AuthContext
                sendiri bisa pakai useNavigate() utk redirect otomatis saat interceptor 401
                global (lib/api.js) mendeteksi sesi sudah tidak valid lagi. */}
            <AuthProvider>
                <Toaster position="top-right" richColors />
                <Routes>
                    <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                    <Route path="/verifikasi-otp" element={<PublicOnly><VerifyOtp /></PublicOnly>} />
                    <Route path="/verifikasi-totp" element={<PublicOnly><VerifyTotp /></PublicOnly>} />
                    {/* TANPA PublicOnly -- halaman ini harus SELALU tampil apapun status login
                        (bukan cuma utk pengunjung yg belum login), krn link reset bisa dibuka di
                        browser yg kebetulan sedang login akun lain. ResetPassword.jsx sendiri yg
                        keluarkan sesi aktif itu (clearSession) sebelum menampilkan form. */}
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/ubah-password" element={<ForcePasswordChange />} />
                    {/* Form publik Peer Assessment -- TANPA PublicOnly & TANPA ProtectedRoute,
                        sama seperti /reset-password: harus selalu bisa dibuka PPDS apapun
                        status login browser itu (bahkan kalau kebetulan ada sesi admin aktif),
                        dan tidak pernah butuh akun AURA sama sekali. */}
                    <Route path="/peer-assessment/isi" element={<PeerAssessmentPublic />} />
                    {/* Survei Kepuasan publik: tanpa login & tanpa sidebar, terbuka juga saat admin sedang login. Alamatnya per
                        periode (/survei-peserta-didik-<kode>, ditangkap RuteSisa di bawah); /survei lama hanya pemberitahuan. */}
                    <Route path="/survei" element={<SurveiTautanBerubah />} />
                    <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/peserta" element={<Peserta />} />
                        <Route path="/peserta/riwayat-import" element={<RiwayatImport />} />
                        <Route path="/peserta/terhapus" element={<ProtectedRoute roles={["super_admin"]}><PesertaTerhapus /></ProtectedRoute>} />
                        <Route path="/prodi" element={<ProtectedRoute roles={["super_admin", "admin_ufp"]}><Prodi /></ProtectedRoute>} />
                        <Route path="/users" element={<ProtectedRoute roles={["super_admin"]}><Users /></ProtectedRoute>} />
                        <Route path="/pengaturan/periode" element={<ProtectedRoute roles={["super_admin"]}><PengaturanPeriode /></ProtectedRoute>} />
                        <Route path="/pengaturan/tarif" element={<ProtectedRoute roles={["super_admin"]}><PengaturanTarif /></ProtectedRoute>} />
                        <Route path="/pengaturan/keamanan-akun" element={<ProtectedRoute roles={["super_admin", "admin_instalasi"]}><PengaturanKeamananAkun /></ProtectedRoute>} />
                        <Route path="/laporan/unit-cost" element={<ProtectedRoute roles={["super_admin", "admin_ufp"]}><LaporanUnitCost /></ProtectedRoute>} />
                        <Route path="/rotasi-klinik" element={<RotasiKlinik />} />
                        <Route path="/orientasi-rs" element={<OrientasiRS />} />
                        <Route path="/rotasi-klinik/dashboard-instalasi" element={<ProtectedRoute roles={["super_admin", "admin_ufp", "admin_instalasi", "admin_prodi", "admin_koas"]}><RotasiKlinikPerInstalasi /></ProtectedRoute>} />
                        <Route path="/data-rotasi-klinik" element={<ProtectedRoute roles={["super_admin"]}><DataRotasiKlinik /></ProtectedRoute>} />
                        <Route path="/peer-assessment" element={<ProtectedRoute roles={["super_admin"]}><PeerAssessmentAdmin /></ProtectedRoute>} />
                        <Route path="/survei-kepuasan" element={<ProtectedRoute roles={PERAN_LIHAT_SURVEI}><SurveiAdmin /></ProtectedRoute>} />
                        <Route path="/audit" element={<ProtectedRoute roles={["super_admin", "admin_ufp"]}><AuditLog /></ProtectedRoute>} />
                        <Route path="/keaktifan" element={<ProtectedRoute roles={PERAN_KEAKTIFAN}><Keaktifan /></ProtectedRoute>} />
                    </Route>
                    <Route path="*" element={<RuteSisa />} />
                </Routes>
            </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    );
}

export default App;
